import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { GeoLocation } from '@grosync/common';
import { searchAddress, reverseGeocode, type AddressResult } from '../../../api/vworld';

const VWORLD_KEY = import.meta.env.VITE_VWORLD_API_KEY;
const DEFAULT_CENTER: [number, number] = [36.5, 127.8]; // 대한민국 중심

// 번들러에서 Leaflet 기본 마커 아이콘 경로 보정 (NodeMap과 동일)
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

export interface LocationPickerProps {
  value: GeoLocation | null;
  onChange: (loc: GeoLocation | null) => void;
}

const FIELD: React.CSSProperties = {
  flex: 1,
  height: 38,
  padding: '0 12px',
  borderRadius: 10,
  border: '1px solid var(--md-sys-color-outline-variant)',
  background: 'var(--md-sys-color-surface-container-lowest)',
  color: 'var(--md-sys-color-on-surface)',
  font: '400 13px/1 var(--gs-font-kr)',
  boxSizing: 'border-box',
};

// 지도 클릭/외부 value 변경 처리. value 좌표가 바뀌면 지도 중심 이동.
function MapBridge({
  value,
  onPick,
}: {
  value: GeoLocation | null;
  onPick: (lat: number, lng: number) => void;
}): null {
  const map = useMap();
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  React.useEffect(() => {
    if (value) map.setView([value.lat, value.lng], Math.max(map.getZoom(), 15));
  }, [value?.lat, value?.lng, map]);
  return null;
}

export function LocationPicker({ value, onChange }: LocationPickerProps): React.ReactElement {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AddressResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const pickSeq = React.useRef(0);

  const runSearch = async (): Promise<void> => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setNote(null);
    const items = await searchAddress(q);
    setResults(items);
    if (!items.length) setNote('검색 결과가 없습니다. 지도를 클릭해 위치를 지정할 수 있습니다.');
    setSearching(false);
  };

  const selectResult = (r: AddressResult): void => {
    onChange({ ...(value ?? {}), lat: r.lat, lng: r.lng, address: r.address });
    setResults([]);
    setQuery(r.address);
  };

  // 지도 클릭/핀 이동 → 좌표 즉시 반영 후 역지오코딩으로 주소 갱신
  // pickSeq ref로 응답 순서를 보장: 최신 픽이 아닌 응답은 무시한다.
  const handleMapPick = (lat: number, lng: number): void => {
    const seq = ++pickSeq.current;
    const base = value ?? {};
    onChange({ ...base, lat, lng });
    void reverseGeocode(lat, lng).then((addr) => {
      if (addr && pickSeq.current === seq) {
        onChange({ ...base, lat, lng, address: addr });
      }
    });
  };

  const center: [number, number] = value ? [value.lat, value.lng] : DEFAULT_CENTER;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* 주소 검색 */}
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void runSearch();
            }
          }}
          placeholder="주소 검색 (예: 세종특별자치시 한누리대로)"
          style={FIELD}
        />
        <button
          type="button"
          onClick={() => void runSearch()}
          disabled={searching || !query.trim()}
          style={{
            height: 38,
            padding: '0 14px',
            borderRadius: 10,
            border: 0,
            cursor: 'pointer',
            background: 'var(--md-sys-color-secondary-container)',
            color: 'var(--md-sys-color-on-secondary-container)',
            font: '600 13px/1 var(--gs-font-kr)',
            whiteSpace: 'nowrap',
          }}
        >
          {searching ? '검색 중…' : '검색'}
        </button>
      </div>

      {/* 검색 결과 */}
      {results.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid var(--md-sys-color-outline-variant)',
            borderRadius: 10,
            overflow: 'hidden',
            maxHeight: 160,
            overflowY: 'auto',
          }}
        >
          {results.map((r, i) => (
            <button
              key={`${r.lat},${r.lng},${i}`}
              type="button"
              onClick={() => selectResult(r)}
              style={{
                textAlign: 'left',
                padding: '9px 12px',
                border: 0,
                borderBottom: i === results.length - 1 ? 0 : '1px solid var(--md-sys-color-outline-variant)',
                background: 'var(--md-sys-color-surface-container-lowest)',
                color: 'var(--md-sys-color-on-surface)',
                font: '400 13px/18px var(--gs-font-kr)',
                cursor: 'pointer',
              }}
            >
              {r.address}
            </button>
          ))}
        </div>
      )}

      {note && (
        <div style={{ font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
          {note}
        </div>
      )}

      {/* 지도 */}
      <div style={{ height: 200, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--md-sys-color-outline-variant)' }}>
        <MapContainer center={center} zoom={value ? 15 : 7} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            url={`/vworld/req/wmts/1.0.0/${VWORLD_KEY}/Base/{z}/{y}/{x}.png`}
            attribution='&copy; VWorld'
            tileSize={256}
            minZoom={6}
            maxZoom={19}
          />
          <MapBridge value={value} onPick={handleMapPick} />
          {value && (
            <Marker
              position={[value.lat, value.lng]}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const { lat, lng } = (e.target as L.Marker).getLatLng();
                  handleMapPick(lat, lng);
                },
              }}
            />
          )}
        </MapContainer>
      </div>

      {/* 좌표 표시 + 지우기 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          className="gs-num"
          style={{ flex: 1, font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}
        >
          {value ? `${value.lat.toFixed(6)}, ${value.lng.toFixed(6)}` : '지도를 클릭하거나 주소를 검색하세요'}
        </span>
        {value && (
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setQuery('');
            }}
            style={{
              height: 30,
              padding: '0 10px',
              borderRadius: 8,
              border: '1px solid var(--md-sys-color-outline-variant)',
              background: 'transparent',
              color: 'var(--md-sys-color-on-surface-variant)',
              font: '500 12px/1 var(--gs-font-kr)',
              cursor: 'pointer',
            }}
          >
            위치 지우기
          </button>
        )}
      </div>
    </div>
  );
}
